import { useState } from "react";
import BackButton from "../../../../components/Button/BackButton";
import FormInput from "../../../../components/Form/FormInput";
import FormSelect from "../../../../components/Form/FormSelect";
import { btn } from "../../../../components/Button/buttonStyles";
import { YES_NO, MANAGER_TYPES } from "./vendorPortalOptions";

const toYesNo = (value) => (value ? "yes" : "no");

const isUrl = (value) => {
  try {
    return ["http:", "https:"].includes(new URL(value).protocol);
  } catch {
    return false;
  }
};

const validate = (form) => {
  const errors = {};
  const days = form.default_payment_terms_days.trim();
  if (!days) errors.default_payment_terms_days = "Default payment terms are required.";
  else if (!/^\d+$/.test(days) || Number(days) > 365)
    errors.default_payment_terms_days = "Enter a whole number of days between 0 and 365.";
  if (!form.invoice_notification_recipient)
    errors.invoice_notification_recipient = "Choose who receives vendor invoice notifications.";
  const url = form.application_form_url.trim();
  if (form.application_form_enabled === "yes" && !url)
    errors.application_form_url = "Application form URL is required when the form is enabled.";
  else if (url && !isUrl(url))
    errors.application_form_url = "Enter a valid URL starting with http:// or https://";
  if (!form.application_notification_recipient)
    errors.application_notification_recipient =
      "Choose who receives new vendor application notifications.";
  return errors;
};

const VendorPortalSettingsForm = ({ settings, onSave, onCancel }) => {
  const [form, setForm] = useState({
    default_payment_terms_days: String(settings.default_payment_terms_days ?? ""),
    vendors_can_create_invoices: toYesNo(settings.vendors_can_create_invoices),
    invoice_notification_recipient: settings.invoice_notification_recipient || "",
    application_form_enabled: toYesNo(settings.application_form_enabled),
    application_form_url: settings.application_form_url || "",
    application_notification_recipient:
      settings.application_notification_recipient || "vendor_manager",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length) return;

    setSaving(true);
    const ok = await onSave({
      default_payment_terms_days: Number(form.default_payment_terms_days),
      vendors_can_create_invoices: form.vendors_can_create_invoices === "yes",
      invoice_notification_recipient: form.invoice_notification_recipient,
      application_form_enabled: form.application_form_enabled === "yes",
      application_form_url: form.application_form_url.trim() || null,
      application_notification_recipient: form.application_notification_recipient,
    });
    setSaving(false);
    if (ok) onCancel();
  };

  return (
    <div>
      <div className="mb-5 flex items-center gap-3">
        <BackButton onClick={onCancel} />
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">
          Edit Vendor Portal Settings
        </h1>
      </div>

      <form
        onSubmit={handleSubmit}
        noValidate
        className="space-y-5 rounded-lg border border-gray-100 bg-white p-5 shadow-sm"
      >
        <div className="grid grid-cols-1 items-start gap-x-5 gap-y-4 md:grid-cols-2">
          <FormInput
            label="Default Payment Terms for Vendor Invoices"
            name="default_payment_terms_days"
            type="number"
            value={form.default_payment_terms_days}
            onChange={handleChange}
            error={errors.default_payment_terms_days}
            suffix="days"
            required
          />
          <FormSelect
            label="Allow Vendors to Create Invoices"
            name="vendors_can_create_invoices"
            value={form.vendors_can_create_invoices}
            onChange={handleChange}
            options={YES_NO}
            required
          />
          <FormSelect
            label="Recipient of Vendor Invoice Related Notifications"
            name="invoice_notification_recipient"
            value={form.invoice_notification_recipient}
            onChange={handleChange}
            options={MANAGER_TYPES}
            error={errors.invoice_notification_recipient}
            required
          />
          <FormSelect
            label="Vendor Application Form Is Enabled"
            name="application_form_enabled"
            value={form.application_form_enabled}
            onChange={handleChange}
            options={YES_NO}
            required
          />
          <FormInput
            label="Vendor Application Form URL"
            name="application_form_url"
            type="url"
            value={form.application_form_url}
            onChange={handleChange}
            error={errors.application_form_url}
            placeholder="https://"
            required={form.application_form_enabled === "yes"}
          />
          <FormSelect
            label="Recipient of Notifications About New Vendor Applications"
            name="application_notification_recipient"
            value={form.application_notification_recipient}
            onChange={handleChange}
            options={MANAGER_TYPES}
            error={errors.application_notification_recipient}
            required
          />
        </div>

        <div className="flex gap-2 border-t border-gray-100 pt-3 text-sm">
          <button type="submit" disabled={saving} className={btn("primary", "sm")}>
            {saving ? "Updating..." : "Update"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className={btn("secondary", "sm")}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default VendorPortalSettingsForm;